import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors, useField, useFormikContext } from 'formik';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import invariant from 'tiny-invariant';

import { Question } from '@synergetx/puppet-interview-types';
import { getPrimaryColor } from '@modules/theme';
import { saveInterview } from '@modules/firebase/interviews';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { useQuestionExpand } from './QuestionExpandContext';
import { prepareInterviewToSavetoDB } from './CreateSurvey/CreateSurveyUtils';
import { DEFAULT_NEW_QUESTION } from './constants';
import { InterviewEdited } from './types';
import SurveySettingsModal from './SurveySettingsModal';

/**
 * Sticky header for the Interview Editor.
 * Combines survey title (click-to-edit), status chip, and all action buttons.
 * Always visible at the top of the content area.
 */
const InterviewHeader = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Paper
            elevation={3}
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                borderTop: 4,
                borderTopColor: getPrimaryColor(),
                borderRadius: 0,
                bgcolor: 'background.paper',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: { xs: 1, md: 1.25 },
                    px: { xs: 1.5, md: 2.5 },
                    gap: { xs: 1, md: 2 },
                    flexWrap: 'nowrap',
                }}
            >
                {/* Left side: Title + Status */}
                <TitleSection isSmallScreen={isSmallScreen} />

                {/* Right side: Actions */}
                <ActionsSection showIconsOnly={isSmallScreen} />
            </Box>
        </Paper>
    );
};

/** Left section with editable title and status chip */
const TitleSection = memo(({ isSmallScreen }: { isSmallScreen: boolean }) => {
    return (
        <Stack
            direction="row"
            spacing={isSmallScreen ? 1 : 1.5}
            sx={{
                alignItems: 'center',
                minWidth: 0,
                flex: '1 1 auto',
            }}
        >
            <InlineEditableTitle isSmallScreen={isSmallScreen} />
            <LockStatusChip />
        </Stack>
    );
});
TitleSection.displayName = 'TitleSection';

/** Clickable chip that shows lock status and toggles on click */
const LockStatusChip = memo(() => {
    const [{ value: isEditingLocked }] = useField<boolean>('isEditingLocked');
    const { fetchInterviews, selectedInterview } = useInterviewOptions();

    const handleToggleLock = useCallback(async () => {
        if (
            window.confirm(
                isEditingLocked
                    ? 'Unlock the survey for editing?'
                    : 'Lock the survey and begin administering?',
            )
        ) {
            invariant(selectedInterview, 'missing selected interview');
            const { groupId, id } = selectedInterview;
            invariant(groupId && id, 'selected interview missing group or id');

            await saveInterview(groupId, {
                id,
                isEditingLocked: !isEditingLocked,
            });

            await fetchInterviews();
        }
    }, [isEditingLocked, selectedInterview, fetchInterviews]);

    const tooltip = isEditingLocked
        ? 'Survey is locked for data collection. Click to unlock for editing.'
        : 'Survey is editable. Click to lock and begin administering.';

    return (
        <Tooltip title={tooltip} placement="bottom">
            <Chip
                size="small"
                label={isEditingLocked ? 'Locked' : 'Editable'}
                icon={isEditingLocked ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                onClick={handleToggleLock}
                variant="outlined"
                sx={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderColor: isEditingLocked ? 'warning.main' : 'success.light',
                    color: isEditingLocked ? 'warning.dark' : 'success.dark',
                    bgcolor: isEditingLocked ? 'warning.50' : 'success.50',
                    '& .MuiChip-icon': {
                        color: 'inherit',
                        fontSize: 16,
                    },
                    '&:hover': {
                        bgcolor: isEditingLocked ? 'warning.100' : 'success.100',
                    },
                }}
            />
        </Tooltip>
    );
});
LockStatusChip.displayName = 'LockStatusChip';

/** Click-to-edit survey title */
const InlineEditableTitle = memo(({ isSmallScreen }: { isSmallScreen: boolean }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [{ value: surveyName }, , { setValue }] = useField<string>('name');
    const [{ value: isLocked }] = useField<boolean>('isEditingLocked');
    const [localValue, setLocalValue] = useState(surveyName);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync local value when surveyName changes externally
    useEffect(() => {
        if (!isEditing) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setLocalValue(surveyName);
        }
    }, [surveyName, isEditing]);

    const handleStartEditing = useCallback(() => {
        if (isLocked) return;
        setIsEditing(true);
        setLocalValue(surveyName);
        setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
    }, [isLocked, surveyName]);

    const handleCommit = useCallback(() => {
        setIsEditing(false);
        if (localValue.trim()) {
            setValue(localValue.trim());
        } else {
            setLocalValue(surveyName); // Revert if empty
        }
    }, [localValue, setValue, surveyName]);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setLocalValue(surveyName);
    }, [surveyName]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleCommit();
            } else if (e.key === 'Escape') {
                handleCancel();
            }
        },
        [handleCommit, handleCancel],
    );

    if (isEditing) {
        return (
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: 'center',
                }}
            >
                {!isSmallScreen && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        Survey:
                    </Typography>
                )}
                <TextField
                    inputRef={inputRef}
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onBlur={handleCommit}
                    onKeyDown={handleKeyDown}
                    size="small"
                    variant="outlined"
                    placeholder="Survey name"
                    sx={{
                        minWidth: { xs: 120, sm: 180, md: 250 },
                        maxWidth: { xs: 180, sm: 280, md: 400 },
                        '& .MuiInputBase-input': {
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            py: 0.75,
                        },
                    }}
                />
            </Stack>
        );
    }

    return (
        <Tooltip title={isLocked ? 'Unlock to edit title' : 'Click to edit survey name'}>
            <Box
                onClick={handleStartEditing}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    minWidth: 0,
                    '&:hover': !isLocked
                        ? {
                              '& .edit-icon': { opacity: 1 },
                              '& .survey-name': { color: 'primary.main' },
                          }
                        : {},
                }}
            >
                {!isSmallScreen && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mr: 1,
                            flexShrink: 0,
                        }}
                    >
                        Survey:
                    </Typography>
                )}
                <Typography
                    variant={isSmallScreen ? 'subtitle1' : 'h6'}
                    className="survey-name"
                    sx={{
                        fontWeight: 600,
                        transition: 'color 0.2s',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: { xs: '120px', sm: '200px', md: '350px', lg: '450px' },
                        opacity: isLocked ? 0.7 : 1,
                    }}
                >
                    {surveyName || 'Untitled Survey'}
                </Typography>
                {!isLocked && (
                    <EditOutlinedIcon
                        className="edit-icon"
                        sx={{
                            ml: 0.5,
                            fontSize: { xs: 14, md: 16 },
                            opacity: 0.5,
                            transition: 'opacity 0.2s',
                            color: 'primary.main',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>
        </Tooltip>
    );
});
InlineEditableTitle.displayName = 'InlineEditableTitle';

/** Right section with action buttons */
const ActionsSection = memo(({ showIconsOnly }: { showIconsOnly: boolean }) => {
    return (
        <Stack
            direction="row"
            spacing={showIconsOnly ? 0.5 : 1}
            sx={{
                alignItems: 'center',
                flexShrink: 0,
            }}
        >
            <ExpandCollapseButton compact={showIconsOnly} />
            <AddQuestionButton compact={showIconsOnly} />
            {!showIconsOnly && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
            <SaveButton compact={showIconsOnly} />
            <SurveySettingsModal />
        </Stack>
    );
});
ActionsSection.displayName = 'ActionsSection';

/** Expand/Collapse all questions button */
const ExpandCollapseButton = memo(({ compact }: { compact: boolean }) => {
    const [{ value: questions }] = useField<Question[] | undefined>('questions');
    const { expandedPositions, expandAll, collapseAll } = useQuestionExpand();
    const hasAnyExpanded = expandedPositions.size > 0;
    const questionCount = questions?.length ?? 0;

    const handleClick = useCallback(() => {
        if (hasAnyExpanded) {
            collapseAll();
        } else {
            expandAll(questionCount);
        }
    }, [hasAnyExpanded, collapseAll, expandAll, questionCount]);

    const tooltip = hasAnyExpanded ? 'Collapse all questions' : 'Expand all questions';
    const Icon = hasAnyExpanded ? ExpandLessIcon : ExpandMoreIcon;

    if (compact) {
        return (
            <Tooltip title={tooltip}>
                <IconButton size="small" onClick={handleClick} color="info">
                    <Icon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltip}>
            <Button
                variant="outlined"
                size="small"
                color="info"
                startIcon={<Icon />}
                onClick={handleClick}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {hasAnyExpanded ? 'Collapse' : 'Expand'}
            </Button>
        </Tooltip>
    );
});
ExpandCollapseButton.displayName = 'ExpandCollapseButton';

/** Add question button */
const AddQuestionButton = memo(({ compact }: { compact: boolean }) => {
    const [{ value: questions }, , { setValue: setAllQuestions }] =
        useField<Question[]>('questions');
    const [{ value: isEditingLocked }] = useField<boolean>('isEditingLocked');
    const { expand } = useQuestionExpand();

    const handleAddQuestion = useCallback(() => {
        // Shift all existing questions down by 1
        const updatedQuestions = questions.map((q, i) => ({
            ...q,
            position: i + 1,
        }));
        const newQuestion = DEFAULT_NEW_QUESTION(0);
        setAllQuestions([newQuestion, ...updatedQuestions]);
        expand(0);
        setTimeout(() => {
            document.getElementById('0')?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
    }, [questions, setAllQuestions, expand]);

    const tooltip = 'Add new question to top';

    if (compact) {
        return (
            <Tooltip title={tooltip}>
                <span>
                    <IconButton
                        size="small"
                        onClick={handleAddQuestion}
                        color="primary"
                        disabled={isEditingLocked}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltip}>
            <span>
                <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuestion}
                    disabled={isEditingLocked}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Add
                </Button>
            </span>
        </Tooltip>
    );
});
AddQuestionButton.displayName = 'AddQuestionButton';

/** The leaf message strings inside one question's error object. */
function messagesFor(questionError: FormikErrors<Question>): string[] {
    return Object.values(questionError).filter(
        (message): message is string => typeof message === 'string' && !!message,
    );
}

/**
 * Turns Formik's error object into lines naming what is missing and where. `interviewSchema`
 * already reports the exact language and field, so surface it rather than "has validation
 * errors" — otherwise authors hunt through every language tab of every question.
 */
function describeErrors(errors: FormikErrors<InterviewEdited>): string[] {
    return Object.keys(errors).flatMap((field) => {
        const value = errors[field as keyof FormikErrors<InterviewEdited>];

        if (field === 'questions' && Array.isArray(value)) {
            return (value as FormikErrors<Question>[]).flatMap((questionError, index) => {
                if (!questionError) return [];

                const messages = messagesFor(questionError);
                return messages.length
                    ? `Question ${index + 1} — ${messages.join('; ')}`
                    : `Question ${index + 1} is missing information`;
            });
        }

        return typeof value === 'string' ? [value] : [];
    });
}

/** Save button */
const SaveButton = memo(({ compact }: { compact: boolean }) => {
    const { handleSubmit, errors, values, resetForm } = useFormikContext<InterviewEdited>();
    const [{ value: isEditingLocked }] = useField<boolean>('isEditingLocked');

    const hasErrors = useMemo(() => {
        return errors && Object.keys(errors as unknown as object).length > 0;
    }, [errors]);

    const errorLines = useMemo(
        () => (hasErrors ? describeErrors(errors) : []),
        [hasErrors, errors],
    );

    const handleSave = useCallback(() => {
        if (hasErrors) {
            if (
                window.confirm(
                    'The survey is missing some information. Save anyway? It must be corrected before the survey can be administered.',
                )
            ) {
                saveInterview(values.groupId, prepareInterviewToSavetoDB(values)).then(
                    (interview) => {
                        if (interview) {
                            resetForm({ values: { ...interview, isEditingLocked: false } });
                        }
                    },
                );
            }
        } else {
            handleSubmit();
        }
    }, [hasErrors, handleSubmit, values, resetForm]);

    const tooltip = isEditingLocked ? (
        'Survey is locked'
    ) : hasErrors ? (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Save — still missing:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {errorLines.map((line) => (
                    <Typography component="li" variant="body2" key={line}>
                        {line}
                    </Typography>
                ))}
            </Box>
        </Box>
    ) : (
        'Save changes'
    );

    if (compact) {
        return (
            <Tooltip title={tooltip}>
                <span>
                    <IconButton
                        size="small"
                        onClick={handleSave}
                        color="primary"
                        disabled={isEditingLocked}
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltip}>
            <span>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isEditingLocked}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Save
                </Button>
            </span>
        </Tooltip>
    );
});
SaveButton.displayName = 'SaveButton';

export default memo(InterviewHeader);
